import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { fmtAmt, fmtDate } from "@/lib/flags";

export default function Sparkline({ points = [], width = 80, height = 24 }) {
  if (!points || points.length < 2) {
    return <span className="text-[10px] text-slate-400 italic">no history</span>;
  }
  const rates = points.map((p) => p.rate);
  const minR = Math.min(...rates);
  const maxR = Math.max(...rates);
  const range = maxR - minR || 1;
  const stepX = points.length > 1 ? width / (points.length - 1) : width;
  const coords = points.map((p, i) => {
    const x = i * stepX;
    const y = height - ((p.rate - minR) / range) * (height - 4) - 2;
    return [x, y];
  });
  const d = coords.map(([x, y], i) => `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`).join(" ");
  const firstPoint = points[0];
  const lastPoint = points[points.length - 1];
  const firstRate = firstPoint.rate;
  const lastRate = lastPoint.rate;
  const trend = lastRate > firstRate ? "up" : lastRate < firstRate ? "down" : "flat";
  const color = trend === "up" ? "#dc2626" : trend === "down" ? "#059669" : "#64748b";
  const pct = firstRate ? (((lastRate - firstRate) / firstRate) * 100) : 0;
  const lastX = coords[coords.length - 1][0];
  const lastY = coords[coords.length - 1][1];
  const arrow = trend === "up" ? "▲" : trend === "down" ? "▼" : "—";
  const pillClass = trend === "up" ? "text-rose-700" : trend === "down" ? "text-emerald-700" : "text-slate-500";
  const verb = trend === "up" ? "climbed" : trend === "down" ? "dropped" : "stayed flat at";

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex items-center gap-1.5 cursor-help" data-testid="sparkline-trend">
          <svg width={width} height={height} className="overflow-visible">
            <path d={d} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx={lastX} cy={lastY} r="2" fill={color} />
          </svg>
          <span className={`text-[10px] font-semibold num ${pillClass}`}>
            <span className="text-slate-400 font-normal mr-0.5">12-mo</span>
            {arrow} {Math.abs(pct).toFixed(1)}%
          </span>
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[280px]">
        <div className="space-y-1 text-xs">
          <div className="font-semibold">12-month price trend</div>
          {trend === "flat" ? (
            <div>Price has {verb} ₹{fmtAmt(firstRate)} over the last {points.length} purchases.</div>
          ) : (
            <div>
              Price has <b>{verb} {Math.abs(pct).toFixed(1)}%</b> over the last {points.length} purchases
              <span className="block text-slate-300 mt-0.5">
                ₹{fmtAmt(firstRate)} on {fmtDate(firstPoint.vdate)} → ₹{fmtAmt(lastRate)} on {fmtDate(lastPoint.vdate)}
              </span>
            </div>
          )}
          <div className="text-[10px] text-slate-400 pt-1 border-t border-white/10">
            Min ₹{fmtAmt(minR)} · Max ₹{fmtAmt(maxR)} · Source: GPurc / GPurci
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
